import { useEffect, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';
import { useNotificationStore } from '../../../store/storeNotifications';

import {
  Box,
  CircularProgress,
  Divider,
  List,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { EmbeddingModelItem } from './EmbeddingModelItem';
import { Info } from '@phosphor-icons/react';

import {
  updateNamespaceName,
  type UpdateNamespaceNameBody,
} from '../../../api/updateNamespaceName';
import { wait } from '../../../util/wait';

import type { TabPanelProps } from './vectorModal';
import type { VectorEmbeddingModel } from '../../../types/vector';
import type { VectorApiParams } from '../../../api/createNamespace';
import type { AxiosError } from 'axios';
import { VectorService } from '../../../service/VectorService';

const embeddingModels: Array<{
  name: VectorEmbeddingModel;
  info: string;
  description: string;
}> = [
    {
      name: 'voyage-3-large',
      info: 'Fast, cost-efficient, 1024-dims',
      description:
        'Great for regulated domains in Finance, Legal, and Healthcare - hit sis proven better performance',
    },
    {
      name: 'text-embedding-3-small',
      info: 'Versatile, 1536-dims. Good all-purpose option.',
      description:
        'Great for regulated domains In Finance, Legal, and Healthcare - this is proven better performance.',
    },
  ];

interface EmbeddingTabPanelProps extends TabPanelProps { }

const inputIcons = {
  idle: null,
  error: null,
  success: null,
  pending: (
    <CircularProgress
      sx={{ position: 'absolute', right: '1rem' }}
      size={16}
      color="inherit"
    />
  ),
};

function EmbeddingTabPanel({ selectedTab }: EmbeddingTabPanelProps) {
  const queryClient = useQueryClient();

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    namespaceId,
    embeddingModel,
    name,
    onEmbeddingModelChange,
    onNameChange,

    isEditing,
  } = useVectorModal();

  const [tempName, setTempName] = useState<string>(name);

  const handleTempNameChange = (newName: string) => {
    setTempName(newName);
  };

  const handleNamespaceNameSubmit = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (['Enter'].includes(evt.key)) {
      handleNamespaceNameUpdate(tempName);
    }
  };

  const handleNamespaceNameUpdate = (newName: string) => {
    const trimmedName = newName.trim();

    if (isEditing) {
      updateNamespaceNameFn({
        body: { newPodName: trimmedName },
        params: { nameSpace: name, id: namespaceId },
      });

      return;
    }

    onNameChange(trimmedName);
  };

  const { mutate: updateNamespaceNameFn, status } = useMutation({
    mutationFn: ({
      body,
      params,
    }: {
      body: UpdateNamespaceNameBody;
      params: VectorApiParams;
    }) => VectorService.updateNamespace({ body, params }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Changing memory pod name from: ${variables.params.nameSpace} to ${variables.body.newPodName}`,
      });
    },
    onError: async (data, variables) => {
      setNotification({
        type: 'error',
        message: `Failed to change memory pod name from: ${variables.params.nameSpace} to ${variables.body.newPodName}`,
      });

      await wait(2000).then(() => {
        setTempName(name);
      });
    },
    onSuccess: (_, variables) => {
      setNotification({
        type: 'success',
        message: `Successfully changed memory pod name to: ${variables.body.newPodName}`,
      });

      return queryClient.invalidateQueries({ queryKey: ['namespaces'] });
    },
  });

  const isNameInputDisabled = status === 'pending';

  useEffect(() => {
    setTempName(name);
  }, [name]);

  return (
    <Box
      role="tabpanel"
      hidden={selectedTab !== 'embedding'}
      id="vertical-tabpanel-embedding"
      aria-labelledby="vertical-tab-embedding"
      sx={{
        width: '100%',
        '&>*+*': {
          marginBlockStart: '2rem !important',
        },
        '&[hidden]': {
          display: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: '900px',
          marginInline: 'auto',
          paddingBlockStart: '1.5rem',
          '&>*+*': {
            marginBlockStart: '1rem !important',
          },
        }}
      >
        <Typography
          variant="h2"
          fontWeight={500}
          fontSize={20}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            lineHeight: '120%',
          }}
        >
          Name your Memory Pod
          <Tooltip
            arrow
            title={
              <>
                <Typography>
                  Create a memory pod and upload your documents.
                </Typography>
                <Typography>
                  Then use the Large Memory Node to query them and get answers
                  easily.
                </Typography>
              </>
            }
          >
            <Info />
          </Tooltip>
        </Typography>

        <TextField
          label="Name"
          aria-label="Pod name"
          fullWidth
          value={tempName}
          disabled={isNameInputDisabled}
          onChange={evt => handleTempNameChange(evt.target.value)}
          onBlur={evt => handleNamespaceNameUpdate(evt.target.value)}
          // onKeyUp={handleNamespaceNameSubmit}
          InputProps={{ startAdornment: inputIcons[status] }}
        />
      </Box>

      <Divider />

      <Box
        sx={{
          width: '900px',
          marginInline: 'auto',
          '&>*+*': {
            marginBlockStart: '2rem !important',
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '0.25rem',
          }}
        >
          <Typography variant="h3">Embedding Method</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Typography color="#475467">
              The embedding method cannot be changed after the memory pod is
              created.
            </Typography>

            <Typography color="#475467">
              If unsure, keep the default.
            </Typography>
          </Box>
        </Box>

        <List sx={{ display: 'grid', gap: '1rem' }}>
          {embeddingModels.map(({ description, info, name }, i) => (
            <EmbeddingModelItem
              key={`${name}-${i}`}
              description={description}
              info={info}
              name={name}
              selectedEmbeddingModel={embeddingModel}
              onEmbeddingModelChange={evt =>
                onEmbeddingModelChange(evt.target.value as VectorEmbeddingModel)
              }
            />
          ))}
        </List>
      </Box>
    </Box>
  );
}

export { EmbeddingTabPanel };
